import { useNavigate, useParams } from "react-router";
import { PlantMark } from "@/app/components/PlantMark";
import { PotCover } from "@/app/components/PotCover";
import { SpeciesCard } from "@/app/components/SpeciesCard";
import {
  BackIcon,
  EditIcon,
  LeafIcon
} from "@/app/components/system/Icons";
import { RoundButton } from "@/app/components/system/RoundButton";
import { usePots } from "@/app/lib/data-context";
import { formatPlantedOn } from "@/app/lib/dates";
import { plantHasDistinctPhoto } from "@/app/lib/plant-image";

export function DetailScreen() {
  const navigate = useNavigate();
  const { id: plantId } = useParams<{ id: string }>();
  const { plants } = usePots();

  const plant = plants.find(({ id }) => id === plantId);
  if (!plant) return null;
  const pot = plant.pot

  // A user-supplied photo (distinct from the species label image) fills the
  // header full-bleed; otherwise we show the centred striped illustration.
  const hasPhoto = plantHasDistinctPhoto(plant);

  return (
    <div className="screen px-0 pt-0">
      <div
        className="relative h-55 grid place-items-center overflow-hidden"
        style={{ background: plant.species?.color ?? "oklch(0.55 0.09 145)" }}
      >
        {hasPhoto && (
          <img
            src={plant.photoImageUrl!}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <RoundButton
          tone="ghost"
          className="absolute top-4.5 left-4.5"
          onClick={() => navigate("/")}
          aria-label="Back"
        >
          <BackIcon size={20} stroke={2} />
        </RoundButton>
        <RoundButton
          tone="ghost"
          className="absolute top-4.5 right-4.5"
          aria-label="Edit"
          onClick={() => navigate(`/plants/${plant.id}/edit`)}
        >
          <EditIcon size={18} />
        </RoundButton>
        {!hasPhoto && (
          <div style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,.2))" }}>
            <PlantMark plant={plant} size={140} />
          </div>
        )}
        <div className="absolute left-0 right-0 bottom-0 flex h-3 [&>span]:flex-1 [&>span]:bg-[linear-gradient(180deg,transparent,rgba(0,0,0,.15))]">
          <span /><span /><span /><span />
        </div>
      </div>

      <div className="pt-6 px-5.5">
        <div className="mb-2">
          <h1 className="display">{plant.nickname}</h1>
          <p className="muted serif-it">{plant.species?.common ?? plant.species?.scientific ?? ""}</p>
        </div>

        <section className="mt-2 mb-5.5 bg-paper border border-line rounded-2xl overflow-hidden">
          <button
            type="button"
            onClick={() => navigate(`/pots/${pot.id}`)}
            className="w-full flex items-center gap-3 px-3.5 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-cream-2"
          >
            <PotCover pot={pot} size="small" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-[17px] m-0 leading-tight tracking-tight text-ink truncate">
                {pot.name}
              </h3>
              <p className="mt-0.5 text-[13px] text-ink-3 inline-flex items-center gap-1">
                <LeafIcon size={13} /> Planted {formatPlantedOn(plant.plantedOn)}
              </p>
            </div>
            <span className="rotate-180 inline-flex text-ink-3"><BackIcon size={14} stroke={2} /></span>
          </button>
        </section>

        <SpeciesCard species={plant.species ?? null} />

      </div>
    </div>
  );
}

